<script>
import { mapState, mapActions, mapGetters } from "vuex";
import { CERTIFICATES_STATUSES } from "@/package/const/certificates-statuses";
import CertificatesStatusBlock from "@/components/certificates/CertificatesStatusBlock.vue";
import MainButton from "@/components/helpers/MainButton.vue";
import CertificatesList from "@/components/certificates/CertificatesList.vue";
import CertificatesBuyModal from "@/components/certificates/CertificatesBuyModal.vue";
import CertificatesConfirmModal from "@/components/certificates/CertificatesConfirmModal.vue";
import MainLoader from "@/components/helpers/MainLoader.vue";

export default {
  name: "CertificatesView",

  components: {
    MainLoader,
    CertificatesConfirmModal,
    CertificatesBuyModal,
    CertificatesList,
    MainButton,
    CertificatesStatusBlock,
  },

  data() {
    return {
      isShowBuyModal: false,
      isShowConfirmModal: false,
      confirmErrorMessage: "",
      selectedStatusId: "all",
      certificateNominal: 0,

      isLoading: false,
      confirmType: "",
      loadingObserver: null,
      page: 1,
      lastPage: 1,

      isDisabledButtonBuy: false,
      isShowPopper: false,

      CERTIFICATES_STATUSES,

      isBuyCertLoading: false,
      isConfirmCertLoading: false,
    };
  },

  computed: {
    ...mapState({
      certificates: (state) => state.certificates.certificates,
      minCertAmount: (state) => state.certificates.minCertAmount,
      certificatesStatuses: (state) => state.certificates.certificatesStatuses,
      user: (state) => state.auth.user,
    }),

    ...mapGetters({
      getUserDistributorAccount: "auth/getUserDistributorAccount",
    }),
  },

  watch: {
    selectedStatusId() {
      this.isLoading = true;
      this.page = 1;

      this.loadCertificates({
        status: this.selectedStatusId === "all" ? "" : this.selectedStatusId,
        page: this.page,
      })
        .then((response) => {
          this.lastPage = response.data.meta.last_page;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    page() {
      if (this.page !== 1) {
        this.isLoading = true;

        this.loadCertificates({
          status: this.selectedStatusId === "all" ? "" : this.selectedStatusId,
          page: this.page,
        }).finally(() => {
          this.isLoading = false;
        });
      }
    },

    lastPage() {
      if (this.lastPage > 1 && this.loadingObserver === null) {
        this.setLoadingObserver();
      }
    },

    minCertAmount(newValue) {
      if (typeof newValue === "number" && !isNaN(newValue)) {
        this.isDisabledButtonBuy =
          this.getUserDistributorAccount?.summary_active < newValue;
      }
    },
  },

  methods: {
    ...mapActions({
      loadCertificates: "certificates/loadCertificates",
      certificateConfirm: "certificates/certificateConfirm",
      storeCertificate: "certificates/storeCertificate",
      createNotification: "createNotification",
      checkUser: "auth/checkUser",
      loadCertificatesNominals: "certificates/loadCertificatesNominals",
    }),

    showPopper() {
      if (this.isDisabledButtonBuy) {
        this.isShowPopper = true;
      }
    },

    confirm(data) {
      this.isBuyCertLoading = true;
      this.certificateNominal = data.nominal;
      this.confirmType = data.type;

      this.certificateConfirm({
        type: data.type,
      })
        .then(() => {
          this.isShowBuyModal = false;
          this.isShowConfirmModal = true;
        })
        .catch(() => {
          if (data.type === "push") {
            this.createNotification({
              title: "Не получилось подтвердить",
              description: "Кажется, у вас ещё нет аккаунта RC PAY",
              type: "error",
            });
          } else if (data.type === "phone") {
            this.createNotification({
              title: "Временный лимит звонков",
              description: "Повторите через 10 минут",
              type: "error",
            });
          } else if (data.type === "whatsapp") {
            this.createNotification({
              title: "Не получилось подтвердить",
              description: "Повторите через 10 минут",
              type: "error",
            });
          } else {
            this.createNotification({
              title: "Что-то пошло не так",
              description: "Повторите через 10 минут",
              type: "error",
            });
          }
        })
        .finally(() => {
          this.isBuyCertLoading = false;
        });
    },

    async buy(code) {
      this.isConfirmCertLoading = true;

      try {
        await this.storeCertificate({
          amount: this.certificateNominal,
          code: code,
        });

        this.isShowConfirmModal = false;
        this.isLoading = true;
        this.page = 1;

        try {
          const response = await this.loadCertificates({
            status:
              this.selectedStatusId === "all" ? "" : this.selectedStatusId,
            page: this.page,
          });

          this.lastPage = response.data.meta.last_page;
          this.setStatuses();

          await Promise.all([
            this.checkUser(),
            this.loadCertificatesNominals(),
          ]);

          this.isDisabledButtonBuy =
            this.getUserDistributorAccount?.summary_active < this.minCertAmount;
        } catch (loadError) {
          console.error("Ошибка при загрузке сертификатов:", loadError);
        } finally {
          this.isLoading = false;
        }
      } catch (err) {
        if (err.response?.data?.message === "certificates.amount.not_allowed") {
          await this.loadCertificatesNominals();
          this.isShowConfirmModal = false;
          return;
        }

        this.confirmErrorMessage = "Код указан неверно";

        setTimeout(() => {
          this.confirmErrorMessage = "";
        }, 5000);
      } finally {
        this.isConfirmCertLoading = false;
      }
    },

    setLoadingObserver() {
      this.loadingObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (this.page >= this.lastPage) {
              return;
            }
            setTimeout(() => {
              this.page++;
            }, 1000);
          }
        });
      });

      this.loadingObserver.observe(
        document.querySelector("#certificates-loader")
      );
    },

    setStatuses() {
      if (this.certificatesStatuses !== null) {
        for (let key in this.certificatesStatuses) {
          this.CERTIFICATES_STATUSES[key].count = this.certificatesStatuses[
            key
          ];
        }
      }
    },

    getCertificatesData() {
      const promises = [
        this.loadCertificates({ status: "", page: 1 }),
        this.loadCertificatesNominals(),
        this.checkUser(),
      ];

      Promise.all(promises)
        .then((responses) => {
          const certificatesResponse = responses[0].data;
          this.lastPage = certificatesResponse.meta.last_page;

          this.setStatuses();

          this.isDisabledButtonBuy =
            this.getUserDistributorAccount?.summary_active < this.minCertAmount;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },

  mounted() {
    this.isLoading = true;

    this.getCertificatesData();
  },

  beforeDestroy() {
    if (this.loadingObserver) {
      this.loadingObserver.unobserve(
        document.querySelector("#certificates-loader")
      );
    }
  },
};
</script>

<template>
  <div class="certificates-view default-content-padding">
    <div class="certificates-view__wrapper">
      <div class="certificates-view__header header">
        <div class="header__statuses">
          <CertificatesStatusBlock
            v-for="(value, name) in CERTIFICATES_STATUSES"
            :key="name"
            class="header__status-block"
            :status="value"
            :is-selected="selectedStatusId === name"
            @select="selectedStatusId = name"
          />
        </div>

        <div class="header__button-wrapper">
          <VDropdown
            popper-class="certificates-view__dropdown"
            :triggers="[]"
            :distance="12"
            :shown.sync="isShowPopper"
          >
            <MainButton
              v-show="!isLoading"
              class="header__button"
              :title="'Купить'"
              :color="'dark'"
              :disabled="isDisabledButtonBuy || isLoading"
              @click="isShowBuyModal = true"
              @mouseenter.native="showPopper"
            />

            <template #popper>
              <div class="certificates-view__popper">
                <h4>Недостаточно средств</h4>
                <p>
                  На дистрибьюторском счете недостаточно средств, необходимо
                  пополнить счет
                </p>

                <router-link :to="{ name: 'Balance' }">
                  Перейти к пополнению
                </router-link>
              </div>
            </template>
          </VDropdown>
        </div>
      </div>

      <MainLoader
        v-if="page === 1 && isLoading"
        class="certificates-view__loader"
      />

      <CertificatesList
        v-else
        :certificates="certificates"
        :selected-status="selectedStatusId"
      />

      <MainLoader
        v-show="page < lastPage && !isLoading"
        id="certificates-loader"
        class="certificates-view__loader certificates-view__loader_margin-top"
      />
    </div>

    <CertificatesBuyModal
      :is-show-modal="isShowBuyModal"
      :phone="`+${user.phone}`"
      :is-buy-cert-loading="isBuyCertLoading"
      @close="isShowBuyModal = false"
      @confirm="confirm"
    />

    <CertificatesConfirmModal
      :is-show-modal="isShowConfirmModal"
      :phone="`+${user.phone}`"
      :confirm-type="confirmType"
      :is-confirm-cert-loading="isConfirmCertLoading"
      :error-message="confirmErrorMessage"
      @close="isShowConfirmModal = false"
      @confirm="buy"
      @resend="confirm({ nominal: certificateNominal, type: confirmType })"
    />
  </div>
</template>

<style lang="scss" scoped>
.certificates-view {
  &__wrapper {
    max-width: 1200px;
    padding-bottom: 48px;
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 40px;

    &__statuses {
      display: grid;
      grid-template-columns: repeat(3, 132px);
      gap: 8px;
    }

    &__button-wrapper {
      display: flex;
      justify-content: flex-end;
      z-index: 100;
      width: 100%;
    }

    &__button {
      width: 140px;
    }

    &__popover {
      > h4 {
        @include body-1;
        color: $dark-primary;
        margin-bottom: 4px;
      }
    }
  }

  &__loader {
    padding: 0;
    justify-content: flex-start;

    &_margin-top {
      margin-top: 24px;
    }
  }
}

@media (max-width: 900px) {
  .certificates-view {
    padding-bottom: calc(48px + 24px);

    .header {
      margin-bottom: 32px;

      &__statuses {
        grid-template-columns: repeat(3, auto);
      }

      &__button-wrapper {
        position: fixed;
        bottom: 24px;
        max-width: calc(100% - 96px);
        display: block;
      }

      &__button {
        width: 100%;
        max-width: none;
      }
    }
  }
}

@media (max-width: 576px) {
  .certificates-view {
    .header {
      &__button-wrapper {
        max-width: calc(100% - 64px);
      }
    }
  }
}

@media (max-width: 425px) {
  .certificates-view {
    .header {
      &__statuses {
        width: 100%;
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 1fr 1fr;
        grid-template-areas:
          "top top"
          ". .";
      }

      &__status-block {
        &:first-child {
          grid-area: top;
        }
      }

      &__button-wrapper {
        max-width: calc(100% - 48px);
      }
    }
  }
}

@media (max-width: 375px) {
  .certificates-view {
    .header {
      &__button-wrapper {
        max-width: calc(100% - 32px);
      }
    }
  }
}
</style>
